import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CircleHelp } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import RosterListButton from "@/components/feature/roster_view/RosterListButton";
import { RosterTabs } from "@/components/feature/roster_view/components/RosterTabs";
import type { RosterViewDate } from "@/services/roster/viewNavigation";

export type RosterViewHeaderProps = {
  readonly date: RosterViewDate;
  readonly onChange: (date: RosterViewDate) => void;
  readonly issuedList: readonly RosterViewDate[];
  readonly scope: "my" | "all";
  readonly myHref: string;
  readonly allHref: string;
  readonly wardOptions: readonly { group_id: string; group_name: string }[];
  readonly wardId: string;
  readonly onSelectWard: (ward: string) => void;
  readonly canSelectWard: boolean;
};

export function RosterViewHeader({
  date,
  onChange,
  issuedList,
  scope,
  myHref,
  allHref,
  wardOptions,
  wardId,
  onSelectWard,
  canSelectWard,
  actions,
}: RosterViewHeaderProps & { readonly actions?: ReactNode }) {
  return (
    <div
      className="flex w-full min-w-0 flex-col gap-3"
      data-testid="roster-view-header"
    >
      <div
        className="flex flex-wrap items-center justify-between gap-3"
        data-testid="roster-view-header-row"
      >
        <h1>
          <RosterListButton
            variant="title"
            issuedList={issuedList}
            currentScheduleDate={date}
            setCurrentScheduleDate={onChange}
            viewLabel={scope === "all" ? "전체 근무표" : "내 근무표"}
          />
        </h1>
        <div className="ml-auto flex items-center gap-4">
          <RosterTabs myHref={myHref} allHref={allHref} />
          {actions}
        </div>
      </div>
      {scope === "all" && canSelectWard && wardOptions.length > 1 && (
        <RosterWardChips
          options={wardOptions}
          value={wardId}
          onChange={onSelectWard}
        />
      )}
    </div>
  );
}
export function RosterWardChips({
  options,
  value,
  onChange,
}: {
  readonly options: readonly { group_id: string; group_name: string }[];
  readonly value: string;
  readonly onChange: (id: string) => void;
}) {
  return (
    <section
      aria-label="전체 근무표 조회 병동"
      className="flex min-w-0 flex-col gap-2"
    >
      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        <span className="font-medium">조회 병동</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="size-6"
              aria-label="조회 병동 안내"
            >
              <CircleHelp aria-hidden="true" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            className="text-sm leading-relaxed break-keep"
            aria-label="조회 병동 권한 안내"
          >
            다른 병동의 전체 근무표는 조회 권한이 있는
            수간호사·그룹관리자·관리자만 볼 수 있습니다.
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {options.map((ward) => (
          <Button
            key={ward.group_id}
            type="button"
            variant="outline"
            aria-pressed={value === ward.group_id}
            aria-label={`${ward.group_name} 근무표 조회`}
            title={ward.group_name}
            className={cn(
              "min-w-20 max-w-80 rounded-full border-2 shadow-none",
              value === ward.group_id
                ? "border-sig bg-transparent font-semibold text-sig hover:bg-row-selected hover:text-sig"
                : "border-border text-muted-foreground"
            )}
            onClick={() => {
              if (value !== ward.group_id) onChange(ward.group_id);
            }}
          >
            <span className="truncate">{ward.group_name}</span>
          </Button>
        ))}
      </div>
    </section>
  );
}
export function RosterViewUnavailable({
  message,
  onRetry,
  showCalendarIcon = false,
  actions,
}: {
  readonly message: string;
  readonly onRetry?: () => void;
  readonly showCalendarIcon?: boolean;
  readonly actions?: ReactNode;
}) {
  return (
    <div
      role={onRetry ? "alert" : "status"}
      className="flex flex-col items-center gap-3 px-5 py-16 text-center"
    >
      {showCalendarIcon && (
        <div
          className="mb-3 flex size-16 shrink-0 items-center justify-center rounded-full bg-[#f1f2f6]"
          aria-hidden="true"
        >
          <img
            src="/asset/icon/wanted-empty-calendar.svg"
            alt=""
            width={28}
            height={28}
            className="size-7"
          />
        </div>
      )}
      <p className="text-lg font-semibold break-keep">{message}</p>
      {onRetry ? (
        <Button variant="outline" onClick={onRetry}>
          다시 시도
        </Button>
      ) : (
        <p className="text-sm text-muted-foreground">
          월 또는 조회 화면을 바꿔 확인할 수 있습니다.
        </p>
      )}
      {actions}
    </div>
  );
}
